import React, { useState } from 'react';
import { changePassword } from '../../libs/api';
import Cookies from 'js-cookie';
import styles from '../../assets/styles/ChangePassword.module.less';
import ThreeBackground from './ThreeBackground';
import { getErrorMessage } from '@/utils/errorHandler';

interface FormData {
  newPassword: string;
  confirmPassword: string;
}

const ChangePassword: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // 清除消息当用户开始输入时
    if (message) {
      setMessage(null);
    }
  };

  const validateForm = (): string | null => {
    if (!formData.newPassword) {
      return '请输入新密码';
    }

    if (formData.newPassword.length < 6) {
      return '密码长度至少为6位';
    }

    if (!formData.confirmPassword) {
      return '请确认新密码';
    }

    if (formData.newPassword !== formData.confirmPassword) {
      return '两次输入的密码不一致';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const userId = Cookies.get('userId');
    if (!userId || userId === 'undefined' || userId === 'null' || userId.trim() === '') {
      setMessage({ type: 'error', text: '未获取到用户信息，请先登录后再修改密码' });
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await changePassword({
        userId: userId.trim(),
        newPassword: formData.newPassword,
      });

      setMessage({ type: 'success', text: '密码修改成功！' });
      setFormData({ newPassword: '', confirmPassword: '' });
    } catch (error: unknown) {
      console.error('修改密码失败:', error);
      const errorMessage = getErrorMessage(error, '修改密码失败，请稍后重试');
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.changePasswordContainer}>
      <ThreeBackground />
      <form
        onSubmit={handleSubmit}
        className={`${styles.changePasswordForm} ${isLoading ? styles.loading : ''}`}
      >
        {/* 密码要求说明 */}
        <div className={styles.passwordRequirements}>
          <p className={styles.formDescription}>为了保护您的账户安全，请设置一个强密码</p>

          <div className={styles.requirementsTitle}>密码要求：</div>
          <ul>
            <li>至少6个字符</li>
            <li>建议包含字母和数字</li>
          </ul>
        </div>

        {/* 消息显示 */}
        {message && (
          <div className={message.type === 'success' ? styles.successMessage : styles.errorMessage}>
            {message.text}
          </div>
        )}

        {/* 新密码输入 */}
        <div className={styles.inputGroup}>
          <label htmlFor="newPassword" className={styles.inputLabel}>
            新密码
          </label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            placeholder="请输入新密码"
            value={formData.newPassword}
            onChange={handleChange}
            className={styles.formInput}
            required
            disabled={isLoading}
            minLength={6}
          />
        </div>

        {/* 确认密码输入 */}
        <div className={styles.inputGroup}>
          <label htmlFor="confirmPassword" className={styles.inputLabel}>
            确认新密码
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            placeholder="请再次输入新密码"
            value={formData.confirmPassword}
            onChange={handleChange}
            className={styles.formInput}
            required
            disabled={isLoading}
            minLength={6}
          />
        </div>

        <button type="submit" className={styles.submitButton} disabled={isLoading}>
          {isLoading ? '' : '修改密码'}
        </button>

        <div className={styles.backLink}>
          <a href="/login">返回登录页面</a>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
